<template>
  <div
    class="credit-card-item"
    :class="{ 'is-clickable': isClickable }"
    @click="handleClick"
  >
    <div class="d-flex justify-content-between align-items-start gap-3">
      <div class="d-flex align-items-center gap-3">
        <div class="card-icon-wrapper">
          <template v-if="hasImageIcon">
            <img :src="icon" :alt="name" class="card-icon-image" />
          </template>

          <template v-else-if="hasClassIcon">
            <i :class="icon"></i>
          </template>

          <template v-else>
            <i class="fal fa-credit-card"></i>
          </template>
        </div>

        <div>
          <p class="card-name mb-0">{{ name }}</p>
          <small class="card-date" v-if="due_date">Vencimento {{ formattedDate }}</small>
        </div>
      </div>

      <span class="card-status">{{ normalizedStatus }}</span>
    </div>

    <div class="mt-3">
      <p class="card-value mb-0">{{ formattedValue }}</p>
    </div>

    <div class="card-progress mt-3">
      <div class="card-progress-bar" :style="{ width: percent + '%' }"></div>
    </div>

    <div class="d-flex justify-content-between mt-2 card-footer-values">
      <span>Limite: {{ formattedLimit }}</span>
      <span>Disponível: {{ formattedAvailable }}</span>
    </div>

    <div v-if="isClickable" class="card-hover-overlay">
      <span class="card-hover-chip">Ver fatura</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["open-invoice"]);

const props = defineProps({
  name: {
    type: String,
    default: "",
  },
  limit: {
    type: [Number, String],
    default: 0,
  },
  value: {
    type: [Number, String],
    default: 0,
  },
  before_value: {
    type: [Number, String],
    default: 0,
  },
  due_date: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    default: "",
  },
  id: {
    type: [Number, String],
    default: null,
  },
  status: {
    type: String,
    default: "",
  },
  sum: {
    type: [Number, String],
    default: 0,
  },
  fixes: {
    type: [Number, String],
    default: 0,
  },
  installments: {
    type: [Number, String],
    default: 0,
  },
  darkmode: {
    type: Boolean,
    default: false,
  },
});

const limitNumber = computed(() => Number(props.limit || 0));
const valueNumber = computed(() => Number((props.value) || 0));
const valueBeforeNumber = computed(() => Number(props.before_value || 0));
const isClickable = computed(() => props.id !== null && props.id !== undefined && props.id !== "");

const valueSumed = computed(() => valueBeforeNumber.value + valueNumber.value);
const availableNumber = computed(() => limitNumber.value - valueSumed.value);

const { t } = useI18n();
const router = useRouter();

const percent = computed(() => {
  if (!limitNumber.value || limitNumber.value <= 0) return 0;
  const raw = (valueSumed.value / limitNumber.value) * 100;
  return Math.max(0, Math.min(raw, 100));
});

const formattedValue = computed(() =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format((valueSumed.value))
);

const formattedLimit = computed(() =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(limitNumber.value)
);

const formattedAvailable = computed(() =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(availableNumber.value)
);

const normalizedStatus = computed(() => {
  if (!props.status) return "Aberto";

  const map = {
    Paid: "Pago",
    Open: "Aberto",
    Closed: "Fechado",
    Overdue: "Atrasado",
    Current: "Atual"
  };

  return map[props.status] || props.status;
});

const hasImageIcon = computed(() => {
  if (!props.icon) return false;

  return (
    props.icon.startsWith("http") ||
    props.icon.startsWith("/") ||
    props.icon.startsWith("data:image") ||
    props.icon.includes(".png") ||
    props.icon.includes(".jpg") ||
    props.icon.includes(".jpeg") ||
    props.icon.includes(".svg") ||
    props.icon.includes(".webp")
  );
});

const hasClassIcon = computed(() => {
  if (!props.icon) return false;
  return !hasImageIcon.value;
});

const formattedDate = computed(() => {
  const raw = props.due_date;
  if (!raw) return "";

  const parsed = new Date(raw + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return raw;

  return new Intl.DateTimeFormat(t('message.global.date_language')).format(parsed);
});

const handleClick = () => {
  if (!isClickable.value) return;

  const payload = {
    id: props.id,
    name: props.name,
    due_date: props.due_date,
    status: props.status,
  };

  emit("open-invoice", payload);

  if (router && props.id !== null && props.id !== undefined && props.id !== "") {
    router.push(`/fatura/${props.id}`);
  }
};
</script>

<style scoped>
.credit-card-item {
  position: relative;
  background: transparent;
  border: 0;
  border-radius: 18px;
  padding: 16px 14px;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.credit-card-item:hover {
  background: rgba(18, 57, 95, 0.035);
  transform: translateY(-1px);
}

.credit-card-item.is-clickable {
  cursor: pointer;
  overflow: hidden;
}

.credit-card-item.is-clickable:hover {
  box-shadow: 0 10px 24px rgba(18, 57, 95, 0.08);
}

.card-icon-wrapper {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(18, 57, 95, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #12395f;
  flex-shrink: 0;
}

.card-icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 14px;
}

.card-icon-wrapper i {
  font-size: 18px;
}

.card-name {
  font-size: 15px;
  font-weight: 800;
  color: #31486d;
  line-height: 1.1;
}

.card-date {
  color: #627693;
  font-size: 13px;
}

.card-status {
  position: relative;
  z-index: 1;
  background: rgba(18, 57, 95, 0.08);
  color: #12395f;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.card-value {
  position: relative;
  z-index: 1;
  font-size: 18px;
  font-weight: 800;
  color: #17212f;
}

.card-progress {
  position: relative;
  z-index: 1;
  height: 6px;
  background: #e9eef5;
  border-radius: 999px;
  overflow: hidden;
}

.card-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #ff7b72 0%, #ef4444 100%);
  border-radius: 999px;
}

.card-footer-values {
  position: relative;
  z-index: 1;
  color: #627693;
  font-size: 13px;
  font-weight: 500;
}

.card-hover-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(18, 57, 95, 0.16);
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: inherit;
  pointer-events: none;
}

.card-hover-chip {
  background: rgba(255, 255, 255, 0.96);
  color: #12395f;
  font-size: 12px;
  font-weight: 800;
  padding: 8px 14px;
  border-radius: 999px;
  box-shadow: 0 8px 18px rgba(18, 57, 95, 0.12);
}

.credit-card-item.is-clickable:hover .card-hover-overlay {
  opacity: 1;
}
</style>
